import { t } from "@lingui/macro";
import {
  startOfMonth,
  isSameMonth,
  differenceInMonths,
  subMonths,
  addMonths,
} from "date-fns";
import React, { useState } from "react";

import {
  Box,
  HStack,
  Text,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { ArrowLeftIcon, ArrowRightIcon } from "../../../icons_v2/general";
import { CalendarBodyProps } from "../types";
import { getParsedDateObj } from "../utils/calendarDateUtils";
import { CalendarMonth } from "./CalendarMonth";

export const CalendarBody = ({
  startDate,
  endDate,
  onSelectDate,
  selectedDate,
  displayHorizontally,
  clubWeekStartsOn,
  twoMonthView = false,
}: CalendarBodyProps) => {
  // this allows the month of the selectedDate to be shown by default if set
  const [firstDisplayedMonth, setFirstDisplayedMonth] = useState(
    getParsedDateObj(selectedDate ?? startDate)
  );

  const calendarStartDate = getParsedDateObj(startDate);
  const calendarEndDate = getParsedDateObj(endDate);
  const nextMonthObj = addMonths(firstDisplayedMonth, 1);

  const doesEndDateSpanToNextMonth = !isSameMonth(
    calendarEndDate,
    calendarStartDate
  );

  const maxCalendarLengthObj = addMonths(calendarEndDate, 1);

  const decrementMonths = () => {
    const preMonth = subMonths(firstDisplayedMonth, 1);
    setFirstDisplayedMonth(preMonth);
  };

  const incrementMonths = () => {
    const nextMonth = addMonths(firstDisplayedMonth, 1);
    const firstDayOfMonth = startOfMonth(nextMonth);
    setFirstDisplayedMonth(firstDayOfMonth);
  };

  const startDateAndFirstDisplayedMonthDiff = differenceInMonths(
    firstDisplayedMonth,
    calendarStartDate
  );

  const leftArrowDisabled =
    startDateAndFirstDisplayedMonthDiff === 0 &&
    !isSameMonth(firstDisplayedMonth, calendarStartDate)
      ? false
      : startDateAndFirstDisplayedMonthDiff === 0;

  const rightArrowDisabled = !displayHorizontally
    ? isSameMonth(firstDisplayedMonth, calendarEndDate)
    : differenceInMonths(maxCalendarLengthObj, nextMonthObj) < 1;

  return (
    <Box
      backgroundColor="surface-secondary"
      borderRadius="4"
      minWidth={displayHorizontally ? 685 : "auto"}
      px={8}
      py={8}
      testID="variable-week-calendar"
    >
      <HStack pb={10} justifyContent="space-between" alignItems="center">
        <Text variant="mainHeading" color="text-primary">{t`Select Date`}</Text>
        {doesEndDateSpanToNextMonth ? (
          <HStack>
            <Pressable
              disabled={leftArrowDisabled}
              onPress={decrementMonths}
              testID="variable-week-calendar-prev-month"
            >
              <Box marginRight={6}>
                <ArrowLeftIcon
                  color={leftArrowDisabled ? "icon-disabled" : "icon-primary"}
                />
              </Box>
            </Pressable>
            <Pressable
              disabled={rightArrowDisabled}
              onPress={incrementMonths}
              testID="variable-week-calendar-next-month"
            >
              <ArrowRightIcon
                color={rightArrowDisabled ? "icon-disabled" : "icon-primary"}
              />
            </Pressable>
          </HStack>
        ) : null}
      </HStack>
      <HStack justifyContent="space-between">
        <CalendarMonth
          selectedDate={selectedDate}
          baseMonthDateObj={firstDisplayedMonth}
          startDate={startDate}
          endDate={endDate}
          onDatePress={onSelectDate}
          displayHorizontally={displayHorizontally}
          isLeft={displayHorizontally}
          clubWeekStartsOn={clubWeekStartsOn}
        />
        {!!displayHorizontally || twoMonthView ? (
          <CalendarMonth
            selectedDate={selectedDate}
            baseMonthDateObj={nextMonthObj}
            startDate={startDate}
            endDate={endDate}
            onDatePress={onSelectDate}
            displayHorizontally={displayHorizontally}
            isRight
            clubWeekStartsOn={clubWeekStartsOn}
          />
        ) : null}
      </HStack>
    </Box>
  );
};
